import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Slider,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowDropUpOutlined, RestartAlt as RestartAltIcon } from "@mui/icons-material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import Premium from "../../../assets/icons/premium.svg";
import "./MobilePlannersFilters.scss";
import { PlannersFilterState } from "../preneed-planners.types";
import { usePlannersFilter } from "../../../common/hooks/plannersUseFilter";

export type MobilePlannersFiltersProps = {
  initialFilters: PlannersFilterState;
  onChange: (filters: PlannersFilterState) => void;
  onCloseDrawer: () => void;
};

export default function MobilePlannersFilters({
  initialFilters,
  onChange,
  onCloseDrawer,
}: MobilePlannersFiltersProps) {
  // CONSTANTS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // STATES
  const { filters, setFilters, resetFilters } = usePlannersFilter(
    initialFilters,
    onChange
  );

  const [activePriceField, setActivePriceField] = useState<
    "min" | "max" | null
  >(null);
  const [activeDistanceField, setActiveDistanceField] = useState<
    "min" | "max" | null
  >(null);

  const [priceSlider, setPriceSlider] = useState(0);
  const [distanceSlider, setDistanceSlider] = useState(0);
  const [priceTouched, setPriceTouched] = useState({ min: false, max: false });
  const [distanceTouched, setDistanceTouched] = useState({
    min: false,
    max: false,
  });

  useEffect(() => {
    if (initialFilters.priceRange) {
      setPriceTouched({ min: true, max: true });
    }
    if (initialFilters.distanceRange) {
      setDistanceTouched({ min: true, max: true });
    }
  }, [initialFilters]);

  const clampRange = (
    min: number,
    max: number,
    limit = 7000
  ): [number, number] => {
    if (min < 0) min = 0;
    if (max > limit) max = limit;

    if (min >= max) min = max;

    return [min, max];
  };

  return (
    <Box className="planners-filters">
      {/* Filters Header */}
      <Box className="planners-filters__header">
        <Typography variant="h6" className="planners-filters__title">
          Filters
        </Typography>
        <IconButton
          onClick={() => {
            resetFilters();
            setPriceSlider(0);
            setDistanceSlider(0);
            setPriceTouched({ min: false, max: false });
            setDistanceTouched({ min: false, max: false });
          }}
          className="planners-filters__reset-btn"
          size="small"
        >
          <RestartAltIcon sx={{ color: "#104145" }} />
        </IconButton>
      </Box>

      {/* ===== PRICE FILTER ===== */}
      <Box className="planners-filters__group">
        <Typography className="planners-filters__group-title">Price Range</Typography>
        <Box className="planners-filters__range-inputs">
          <TextField
            type="number"
            placeholder="Min"
            value={priceTouched.min ? filters.priceRange?.[0] ?? "" : ""}
            onFocus={() => setActivePriceField("min")}
            onChange={(e) => {
              setPriceTouched((t) => ({ ...t, min: true }));
              let newMin = Number(e.target.value);
              const maxVal = filters.priceRange?.[1] ?? 7000;
              const [fixedMin, fixedMax] = clampRange(newMin, maxVal);
              setFilters((prev) => ({ ...prev, priceRange: [fixedMin, fixedMax] }));
            }}
            size="small"
            fullWidth
          />
          <TextField
            type="number"
            placeholder="Max"
            value={priceTouched.max ? filters.priceRange?.[1] ?? "" : ""}
            onFocus={() => setActivePriceField("max")}
            onChange={(e) => {
              setPriceTouched((t) => ({ ...t, max: true }));
              let newMax = Number(e.target.value);
              const minVal = filters.priceRange?.[0] ?? 0;
              const [fixedMin, fixedMax] = clampRange(minVal, newMax);
              setFilters((prev) => ({ ...prev, priceRange: [fixedMin, fixedMax] }));
            }}
            size="small"
            fullWidth
          />
        </Box>
        <Box sx={{ px: 1, py: 1 }}>
          <Slider
            value={priceSlider}
            onChange={(_, val) => {
              const newVal = val as number;
              setFilters((prev) => {
                let [minPrice, maxPrice] = prev.priceRange ?? [0, 7000];
                if (activePriceField === "min") {
                  [minPrice, maxPrice] = clampRange(newVal, maxPrice);
                  setPriceTouched((t) => ({ ...t, min: true }));
                } else {
                  [minPrice, maxPrice] = clampRange(minPrice, newVal);
                  setPriceTouched((t) => ({ ...t, max: true }));
                }
                return { ...prev, priceRange: [minPrice, maxPrice] };
              });
              setPriceSlider(newVal);
            }}
            min={0}
            max={7000}
            step={100}
            className="planners-filters__slider"
          />
        </Box>
      </Box>

      {/* ===== DISTANCE FILTER ===== */}
      <Box className="planners-filters__group">
        <Typography className="planners-filters__group-title">Distance</Typography>
        <Box className="planners-filters__range-inputs">
          <TextField
            type="number"
            placeholder="Min"
            value={distanceTouched.min ? filters.distanceRange?.[0] ?? "" : ""}
            onFocus={() => setActiveDistanceField("min")}
            onChange={(e) => {
              setDistanceTouched((t) => ({ ...t, min: true }));
              let newMin = Number(e.target.value);
              const maxVal = filters.distanceRange?.[1] ?? 100;
              const [fixedMin, fixedMax] = clampRange(newMin, maxVal, 100);
              setFilters((prev) => ({ ...prev, distanceRange: [fixedMin, fixedMax] }));
            }}
            size="small"
            fullWidth
          />
          <TextField
            type="number"
            placeholder="Max"
            value={distanceTouched.max ? filters.distanceRange?.[1] ?? "" : ""}
            onFocus={() => setActiveDistanceField("max")}
            onChange={(e) => {
              setDistanceTouched((t) => ({ ...t, max: true }));
              let newMax = Number(e.target.value);
              const minVal = filters.distanceRange?.[0] ?? 0;
              const [fixedMin, fixedMax] = clampRange(minVal, newMax, 100);
              setFilters((prev) => ({ ...prev, distanceRange: [fixedMin, fixedMax] }));
            }}
            size="small"
            fullWidth
          />
        </Box>
        <Box sx={{ px: 1, py: 1 }}>
          <Slider
            value={distanceSlider}
            onChange={(_, val) => {
              const newVal = val as number;
              setFilters((prev) => {
                let [minDist, maxDist] = prev.distanceRange ?? [0, 100];
                if (activeDistanceField === "min") {
                  [minDist, maxDist] = clampRange(newVal, maxDist, 100);
                  setDistanceTouched((t) => ({ ...t, min: true }));
                } else {
                  [minDist, maxDist] = clampRange(minDist, newVal, 100);
                  setDistanceTouched((t) => ({ ...t, max: true }));
                }
                return { ...prev, distanceRange: [minDist, maxDist] };
              });
              setDistanceSlider(newVal);
            }}
            min={0}
            max={100}
            step={5}
            className="planners-filters__slider"
          />
        </Box>
      </Box>

      {/* ===== STAR RATING FILTER ===== */}
      <Box className="planners-filters__group">
        <Typography className="planners-filters__group-title">Star Rating</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          <Button
            sx={{
              border: "1px solid #BFC0C2",
              color: filters.sortBy === "rating_high_to_low" ? "#fff" : "#4B4F50",
              bgcolor: filters.sortBy === "rating_high_to_low" ? "#ebd28d" : "transparent",
              textTransform: "capitalize",
              fontSize: "12px",
              padding: "4px 8px",
            }}
            endIcon={<StarRoundedIcon sx={{ fontSize: "16px !important", color: filters.sortBy === "rating_high_to_low" ? "#fff" : "#9aa0a8" }} />}
            onClick={() => setFilters((prev) => ({ ...prev, sortBy: prev.sortBy === "rating_high_to_low" ? undefined : "rating_high_to_low" }))}
          >
            Top Rated
          </Button>

          {[5, 4, 3].map((star) => {
            const isSelected = filters.ratings?.includes(star);
            return (
              <Button
                key={star}
                sx={{
                  border: "1px solid #BFC0C2",
                  color: isSelected ? "#fff" : "#4B4F50",
                  bgcolor: isSelected ? "#ebd28d" : "transparent",
                  textTransform: "capitalize",
                  fontSize: "12px",
                  padding: "4px 8px",
                  minWidth: "auto",
                }}
                onClick={() => setFilters((prev) => {
                  const current = prev.ratings || [];
                  return {
                    ...prev,
                    ratings: isSelected ? current.filter((r) => r !== star) : [...current, star],
                  };
                })}
                endIcon={<StarRoundedIcon sx={{ fontSize: "16px !important", color: isSelected ? "#fff" : "#9aa0a8" }} />}
              >
                {star}
              </Button>
            );
          })}
        </Box>
        <Box sx={{ mt: 1 }}>
          <Button
            sx={{
              border: "1px solid #BFC0C2",
              color: "#4B4F50",
              textTransform: "capitalize",
              fontSize: "12px",
              padding: "4px 8px",
            }}
            endIcon={<img src={Premium} alt="Premium" style={{ width: 16, height: 16 }} />}
          >
            Premium
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
